import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import client, { endpoints, errText } from '../../api/client';
import StatusBadge from '../../components/StatusBadge';
import { money, dt } from '../../lib/format';

// Scoping a quote: lines in, total computed. The total is deliberately NOT an
// input — the server recomputes it from the lines, and showing a separately
// editable total here would invite a quote whose sum does not match its own lines.
export default function QuoteDetail() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [services, setServices] = useState([]);
  const [areas, setAreas] = useState([]);
  const [lines, setLines] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.get(endpoints.quotes.get(ref));
      setQ(r.data.quote);
      setLines((r.data.quote.lines || []).map((l) => ({ label: l.label, qty: l.qty, unit: l.unitMinor / 100 })));
      const [s, a] = await Promise.all([client.get(endpoints.services.all), client.get(endpoints.areas.all)]);
      setServices(s.data.items || []);
      setAreas(a.data.items || []);
    } catch (e) { setErr(errText(e)); }
  }, [ref]);

  useEffect(() => { load(); }, [load]);

  const act = (fn) => async () => {
    setBusy(true); setErr(''); setMsg('');
    try { await fn(); await load(); }
    catch (e) { setErr(errText(e)); }
    setBusy(false);
  };

  if (err && !q) return <section className="section"><p className="alert alert-err">{err}</p></section>;
  if (!q) return <section className="section"><p className="muted">Loading…</p></section>;

  const previewTotal = lines.reduce((s, l) => s + Math.round((Number(l.qty) || 0) * (Number(l.unit) || 0) * 100), 0);
  const previewDeposit = Math.floor((previewTotal * (Number(q.depositPct) || 0)) / 100);

  return (
    <section className="section">
      <div className="section-head">
        <h1>{q.reference} <StatusBadge status={q.status} kind="quote" /></h1>
        <Link to="/ops/quotes">← All quotes</Link>
      </div>
      {err && <p className="alert alert-err">{err}</p>}
      {msg && <p className="alert alert-ok">{msg}</p>}

      <div className="cols">
        <div className="col">
          <div className="card">
            <h2>What they asked for</h2>
            <dl className="kv">
              <dt>Client</dt><dd>{q.company || '—'}</dd>
              <dt>Contact</dt><dd>{q.contact?.name}{q.contact?.role ? `, ${q.contact.role}` : ''}<br />
                <a href={`tel:${q.contact?.phone}`}>{q.contact?.phone}</a> · <a href={`mailto:${q.contact?.email}`}>{q.contact?.email}</a></dd>
              <dt>City</dt><dd>{q.city || <span className="muted">outside current coverage</span>}</dd>
              <dt>Property</dt><dd>{q.propertyType || '—'}{q.sizeSqm ? ` · ${q.sizeSqm} sqm` : ''}{q.units ? ` · ${q.units} units` : ''}</dd>
              <dt>Timeline</dt><dd>{q.timeline || '—'}</dd>
              <dt>Recurring</dt><dd>{q.recurringInterest ? 'Interested in a maintenance plan' : 'Not asked for'}</dd>
              <dt>Site conditions</dt><dd className="prewrap">{q.siteConditions || '—'}</dd>
              <dt>Details</dt><dd className="prewrap">{q.details || '—'}</dd>
            </dl>
          </div>

          <div className="card">
            <h2>Scope &amp; price</h2>
            <table className="table table-tight">
              <thead><tr><th>Line</th><th>Qty</th><th>Unit price</th><th></th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td><input value={l.label} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} /></td>
                    <td><input type="number" min="0" step="1" value={l.qty} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} /></td>
                    <td><input type="number" min="0" step="1" value={l.unit} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} /></td>
                    <td><button className="link-btn" onClick={() => setLines(lines.filter((_, j) => j !== i))}>remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn-ghost" onClick={() => setLines([...lines, { label: '', qty: 1, unit: 0 }])}>Add line</button>

            <dl className="kv total-preview">
              <dt>Total</dt><dd>{money(previewTotal, q.currency)}</dd>
              <dt>Deposit ({q.depositPct}%)</dt><dd>{money(previewDeposit, q.currency)}</dd>
              <dt>Balance</dt><dd>{money(previewTotal - previewDeposit, q.currency)}</dd>
            </dl>

            <form onSubmit={(e) => {
              e.preventDefault();
              const f = e.target.elements;
              act(() => client.put(endpoints.quotes.scope(q.reference), {
                lines: lines.filter((l) => l.label),
                depositPct: Number(f.depositPct.value),
                proposedSchedule: f.proposedSchedule.value,
                crewNote: f.crewNote.value,
                validUntil: f.validUntil.value || undefined,
              }))();
            }}>
              <label>Deposit %<input name="depositPct" type="number" min="0" max="100" defaultValue={q.depositPct} /></label>
              <label>Proposed schedule<input name="proposedSchedule" defaultValue={q.proposedSchedule} placeholder="e.g. three consecutive days before handover" /></label>
              <label>Crew<input name="crewNote" defaultValue={q.crewNote} placeholder="e.g. eight-person crew with industrial equipment" /></label>
              <label>Valid until<input name="validUntil" type="date" defaultValue={q.validUntil ? String(q.validUntil).slice(0, 10) : ''} /></label>
              <button className="btn-primary" disabled={busy}>Save scope</button>
            </form>
          </div>
        </div>

        <div className="col col-side">
          <div className="card">
            <h2>Consultation call</h2>
            <p className="muted small">{q.consultAt ? `Booked for ${dt(q.consultAt)}` : 'Not booked yet.'}</p>
            <form className="inline-form" onSubmit={(e) => {
              e.preventDefault();
              const at = e.target.elements.at.value;
              const note = e.target.elements.note.value;
              act(() => client.put(endpoints.quotes.consult(q.reference), { at: at || undefined, note }))();
            }}>
              <input name="at" type="datetime-local" defaultValue={q.consultAt ? String(q.consultAt).slice(0, 16) : ''} />
              <input name="note" placeholder="what you learned" defaultValue={q.consultNote} />
              <button className="btn-ghost" disabled={busy}>Save</button>
            </form>
          </div>

          <div className="card">
            <h2>Send it</h2>
            <button className="btn-primary" disabled={busy || !q.lines?.length}
                    onClick={async () => {
                      setBusy(true); setErr(''); setMsg('');
                      try {
                        const r = await client.put(endpoints.quotes.send(q.reference));
                        // If SMTP is off the API says so — the console must repeat
                        // that rather than let a dispatcher assume it went out.
                        setMsg(r.data.emailed ? 'Quote emailed to the client.' : `Marked as sent, but the email did NOT go out (${r.data.warning}). Send it by WhatsApp.`);
                        await load();
                      } catch (e) { setErr(errText(e)); }
                      setBusy(false);
                    }}>
              Send quote
            </button>
            {!q.lines?.length && <p className="muted small">Add at least one priced line first.</p>}
            {q.sentAt && <p className="muted small">Last sent {dt(q.sentAt)}</p>}
          </div>

          <div className="card">
            <h2>Decision</h2>
            {['accepted', 'declined'].includes(q.status) ? (
              <p>Marked <strong>{q.status}</strong> {dt(q.decidedAt)}
                {q.booking && <> · <Link to={`/ops/bookings/${q.booking}`}>view the job</Link></>}</p>
            ) : (
              <form onSubmit={(e) => {
                e.preventDefault();
                const f = e.target.elements;
                act(async () => {
                  const r = await client.put(endpoints.quotes.decision(q.reference), {
                    decision: 'accepted',
                    date: f.date.value || undefined,
                    time: f.time.value || undefined,
                    city: f.city.value || undefined,
                    serviceKey: f.serviceKey.value || undefined,
                    address: f.address.value || undefined,
                  });
                  if (r.data.booking) navigate(`/ops/bookings/${r.data.booking.reference}`);
                })();
              }}>
                <p className="muted small">Accepting with a date creates the job, carrying the scoped price and the {q.depositPct}% deposit across.</p>
                <label>Service
                  <select name="serviceKey" defaultValue={q.serviceKey}>
                    <option value="">—</option>
                    {services.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
                  </select>
                </label>
                <label>City
                  <select name="city" defaultValue={q.area || ''}>
                    <option value="">—</option>
                    {areas.map((a) => <option key={a.slug} value={a.slug}>{a.city}</option>)}
                  </select>
                </label>
                <label>Date<input name="date" type="date" /></label>
                <label>Start time<input name="time" placeholder="08:00" /></label>
                <label>Site address<input name="address" placeholder="where the crew goes" /></label>
                <div className="btn-row">
                  <button className="btn-primary" disabled={busy}>Accept</button>
                  <button type="button" className="btn-danger" disabled={busy}
                          onClick={act(() => client.put(endpoints.quotes.decision(q.reference), { decision: 'declined' }))}>
                    Declined
                  </button>
                </div>
              </form>
            )}
          </div>

          <div className="card">
            <h2>History</h2>
            <ol className="history">
              {(q.history || []).map((h) => (
                <li key={h._id}>
                  <strong>{h.to.replace(/_/g, ' ')}</strong> <span className="muted small">{h.by} · {dt(h.at)}</span>
                  {h.note && <div className="muted small">{h.note}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
